import { useState, useRef, useCallback } from "react";
import { confirmDialog } from "primereact/confirmdialog";
import NotificationPopUp from "util/NotificationPopUp";
import Utils from "util/Utils";
import useDataHubStore from "store/dataHub-store";
import { JobRunnerTabTypes } from "../JobRunner";
import { useJobRunnerApis } from "./useJobRunnerApis";
import { DialogType } from "./useJobRunnerDialogs";
import {
  CodeFileInfo,
  ExecutionResultDTO,
  JobInfoDTO,
  PythonTaskData,
  TaskTemplateInfo,
  ScheduledTaskExtendedDTO,
} from "../../../../serviceapi/jobRunnerApi";

const computeLastExecutions = (
  executions: ExecutionResultDTO[]
): Record<string, ExecutionResultDTO> => {
  const lastExecutions: Record<string, ExecutionResultDTO> = {};
  for (const execution of executions) {
    const current = lastExecutions[execution.taskId];
    if (
      !current ||
      new Date(execution.startTime).getTime() >
        new Date(current.startTime).getTime()
    ) {
      lastExecutions[execution.taskId] = execution;
    }
  }
  return lastExecutions;
};

export const useJobRunnerActions = (
  setOpenDialog: (dialog: DialogType) => void,
  closeDialog: () => void
) => {
  const { apiScheduled, apiJobs, apiJobRunnerLogs } = useJobRunnerApis();

  const [listLoad, setListLoad] = useState(true);
  const [jobs, setJobs] = useState<JobInfoDTO[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobInfoDTO | null>(null);

  const [currentTask, setCurrentTask] =
    useState<ScheduledTaskExtendedDTO | null>(null);
  const [currentTab, setCurrentTab] = useState(JobRunnerTabTypes.CONFIG);

  const [codeFilesList, setCodeFilesList] = useState<CodeFileInfo[]>([]);
  const [selectedCodeFile, setSelectedCodeFile] =
    useState<CodeFileInfo | null>(null);
  const [taskCode, setTaskCode] = useState<any>(null);
  const [encoderKey, setEncoderKey] = useState(0);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [newFileName, setNewFileName] = useState("");

  const [readScript, setReadScript] = useState(false);

  const [currentTestData, setCurrentTestData] =
    useState<PythonTaskData | null>(null);
  const [currentExecutionsData, setCurrentExecutionsData] = useState<
    ExecutionResultDTO[] | null
  >(null);
  const [lastExecutions, setLastExecutions] = useState<
    Record<string, ExecutionResultDTO>
  >({});
  const [logsInitialTaskId, setLogsInitialTaskId] = useState<string | null>(
    null
  );

  const [isConfigEdited, setIsConfigEdited] = useState(false);
  const [isCodeEdited, setIsCodeEdited] = useState(false);
  const [isTemplateInfoEdited, setIsTemplateInfoEdited] = useState(false);

  const encoderRef = useRef<any>(null);
  const jobRunnerViewRef = useRef<any>(null);

  const templateInfo = currentTask?.templateInfo ?? null;

  // --- Data fetching ---

  const fetchTaskDetails = useCallback(
    async (taskId: string) => {
      try {
        const response = await apiScheduled.getAirflowTaskConfig("1", taskId);
        setCurrentTask(response.data);
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          "Failed to fetch task details."
        );
      }
    },
    [apiScheduled]
  );

  const fetchLastExecutions = useCallback(async () => {
    try {
      const response = await apiJobRunnerLogs.getExecutions({});
      setLastExecutions(computeLastExecutions(response.data));
    } catch {
      // Status icons are best-effort; keep whatever was fetched last.
    }
  }, [apiJobRunnerLogs]);

  const fetchJobs = useCallback(async () => {
    try {
      setListLoad(true);
      const response = await apiJobs.getJobs();
      const fetchedJobs = response.data;
      setJobs(fetchedJobs);

      if (fetchedJobs.length > 0) {
        const firstJob = fetchedJobs[0];
        setSelectedJob(firstJob);
        fetchTaskDetails(firstJob.taskId);
      }

      fetchLastExecutions();
    } catch (error) {
      NotificationPopUp.handleGenericError(error, "Failed to fetch jobs.");
    } finally {
      setListLoad(false);
    }
  }, [apiJobs, fetchTaskDetails, fetchLastExecutions]);

  const fetchCodeFilesList = useCallback(async () => {
    try {
      const response = await apiScheduled.listCodeFiles();
      setCodeFilesList(response.data);
      return response.data;
    } catch (error) {
      NotificationPopUp.handleGenericError(
        error,
        "Failed to fetch code files."
      );
    }
  }, [apiScheduled]);

  // --- Code operations ---

  const downloadTaskCode = useCallback(
    async (codeFileId: number) => {
      try {
        const response = await apiScheduled.downloadTaskCode(codeFileId, {
          headers: { Accept: "application/octet-stream" },
          responseType: "text",
        });

        const pythonCode = String(response.data);
        setTaskCode(Utils.base64Encode(pythonCode));
        setEncoderKey((prev) => prev + 1);
        setIsCodeEdited(false);
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          "Failed to fetch code file."
        );
      }
    },
    [apiScheduled]
  );

  const checkIfFileNameExists = useCallback(
    async (fileName: string): Promise<boolean> => {
      const fileList = await apiScheduled.listCodeFiles();
      if (fileList.data.some((file: CodeFileInfo) => file.name === fileName)) {
        NotificationPopUp.show(
          "A file with the same name already exists.",
          "warn"
        );
        return true;
      }
      return false;
    },
    [apiScheduled]
  );

  // --- Run task ---

  const runTask = useCallback(
    async (job: JobInfoDTO) => {
      try {
        const response = await apiScheduled.getAirflowTaskConfig("1", job.taskId);
        await apiScheduled.updateAirflowTaskConfig(response.data as any,true);
        NotificationPopUp.show("Task triggered successfully!", "success");
      } catch (error) {
        NotificationPopUp.handleGenericError(error, "Failed to run task.");
      }
    },
    [apiScheduled]
  );

  // --- Save operations ---

  const saveTaskConfig = useCallback(
    async (taskConfig?: ScheduledTaskExtendedDTO) => {
      try {
        const config = jobRunnerViewRef.current?.editor?.getValue();
        const parsedConfig = taskConfig ?? JSON.parse(config);
        setCurrentTask(parsedConfig);
        await apiScheduled.updateAirflowTaskConfig(parsedConfig, true);

        if (selectedJob) {
          setSelectedJob({
            ...selectedJob,
            taskTitle: parsedConfig.title,
          });
        }

        setIsConfigEdited(false);
        NotificationPopUp.show("Changes saved successfully!", "success");
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          "Failed to save task config."
        );
      }
    },
    [apiScheduled, selectedJob]
  );

  const saveTaskCode = useCallback(async () => {
    if (!selectedCodeFile?.id) {
      NotificationPopUp.show("No code file selected.", "warn");
      return;
    }

    try {
      const decodedScript = Utils.base64Decode(taskCode);
      const pythonFile = new File([decodedScript], selectedCodeFile.name, {
        type: "text/x-python",
      });

      await apiScheduled.updateTaskCode(selectedCodeFile.id, pythonFile);
      NotificationPopUp.show("Code file updated successfully!", "success");
      setIsCodeEdited(false);
    } catch (error) {
      NotificationPopUp.handleGenericError(
        error,
        "Failed to update code file."
      );
    }
  }, [apiScheduled, selectedCodeFile, taskCode]);

  const saveTemplateInfo = useCallback(async () => {
    try {
      const templateInfoConfig = jobRunnerViewRef.current?.editor?.getValue();
      const parsedConfig = JSON.parse(templateInfoConfig);
      const updatedTask = {
        ...currentTask,
        templateInfo: parsedConfig as TaskTemplateInfo,
      };

      setCurrentTask(updatedTask as ScheduledTaskExtendedDTO);
      await apiScheduled.updateAirflowTaskConfig(updatedTask as any, false);

      setIsTemplateInfoEdited(false);
      NotificationPopUp.show("Changes saved successfully!", "success");
    } catch (error) {
      NotificationPopUp.handleGenericError(
        error,
        "Failed to save template info."
      );
    }
  }, [apiScheduled, currentTask]);

  const saveCurrentTab = useCallback(() => {
    if (currentTab === JobRunnerTabTypes.CODE) return saveTaskCode();
    if (currentTab === JobRunnerTabTypes.TEMPLATE) return saveTemplateInfo();
    return saveTaskConfig();
  }, [currentTab, saveTaskCode, saveTemplateInfo, saveTaskConfig]);

  // --- Code file assignment ---

  const loadTaskCode = useCallback(
    async (codeFile?: CodeFileInfo) => {
      const codeFileId = codeFile?.id ?? selectedCodeFile?.id;
      if (codeFileId === undefined) return;

      closeDialog();
      await downloadTaskCode(codeFileId);

      if (currentTask) {
        const updatedTask = { ...currentTask, codeFileId };
        setCurrentTask(updatedTask);
        saveTaskConfig(updatedTask);
        NotificationPopUp.show("Code file loaded successfully!", "success");
      }
    },
    [
      selectedCodeFile,
      currentTask,
      closeDialog,
      downloadTaskCode,
      saveTaskConfig,
    ]
  );

  const uploadTaskCode = useCallback(async () => {
    if (!fileToUpload) {
      NotificationPopUp.show("Please select a file to upload.", "warn");
      return;
    }

    const fileExists = await checkIfFileNameExists(fileToUpload.name);
    if (fileExists) return;

    try {
      await apiScheduled.uploadTaskCode(fileToUpload);
      const updatedFileList = await fetchCodeFilesList();
      await loadTaskCode(
        updatedFileList?.find((f) => f.name === fileToUpload.name)
      );
      NotificationPopUp.show("File uploaded successfully!", "success");
    } catch (error) {
      NotificationPopUp.handleGenericError(error, "Failed to upload file.");
    }
  }, [
    fileToUpload,
    apiScheduled,
    checkIfFileNameExists,
    fetchCodeFilesList,
    loadTaskCode,
  ]);

  const createBlankPythonFile = useCallback(async () => {
    if (!newFileName.trim()) {
      NotificationPopUp.show("Please enter a file name.", "warn");
      return;
    }

    const fileExists = await checkIfFileNameExists(newFileName);
    if (fileExists) return;

    const fileNameWithExtension = `${newFileName.replace(/\s+/g, "-")}.py`;
    const file = new File([""], fileNameWithExtension, { type: "text/plain" });

    try {
      await apiScheduled.uploadTaskCode(file);
      closeDialog();
      setNewFileName("");

      const updatedFileList = await apiScheduled.listCodeFiles();
      const uploadedFile = updatedFileList.data.find(
        (f: CodeFileInfo) => f.name === fileNameWithExtension
      );

      if (uploadedFile) {
        setSelectedCodeFile(uploadedFile);
        await loadTaskCode(uploadedFile);
      }

      NotificationPopUp.show("File created successfully!", "success");
    } catch (error) {
      NotificationPopUp.handleGenericError(error, "Failed to create file.");
    }
  }, [newFileName, apiScheduled, checkIfFileNameExists, closeDialog, loadTaskCode]);

  const createEmptyFileForJob = useCallback(async () => {
    if (!selectedJob) return;

    const fileName = `${selectedJob.taskId.replace(/\s+/g, "-")}.py`;
    const fileExists = await checkIfFileNameExists(fileName);
    if (fileExists) return;

    const file = new File([""], fileName, { type: "text/plain" });
    try {
      await apiScheduled.uploadTaskCode(file);
      const updatedFileList = await apiScheduled.listCodeFiles();
      const uploadedFile = updatedFileList.data.find(
        (f: CodeFileInfo) => f.name === fileName
      );

      if (uploadedFile) {
        setSelectedCodeFile(uploadedFile);
        await loadTaskCode(uploadedFile);
      }

      NotificationPopUp.show("File created successfully!", "success");
    } catch (error) {
      NotificationPopUp.handleGenericError(error, "Failed to create file.");
    }
  }, [selectedJob, apiScheduled, checkIfFileNameExists, loadTaskCode]);

  // --- Job operations ---

  const handleToggleJobRunner = useCallback(
    async (jobRunnerId?: string) => {
      if (!jobRunnerId || !currentTask) return;

      const updatedConfig = {
        ...currentTask,
        isSkipped: !currentTask.isSkipped,
      };
      await apiScheduled.updateAirflowTaskConfig(updatedConfig as any, false);

      if (selectedJob) {
        setSelectedJob({ ...selectedJob, enabled: !selectedJob.enabled });
      }
    },
    [apiScheduled, currentTask, selectedJob]
  );

  const isAnyTabEdited = isConfigEdited || isCodeEdited || isTemplateInfoEdited;

  const switchToJob = useCallback(
    async (job: JobInfoDTO) => {
      setSelectedCodeFile(null);
      setSelectedJob(job);
      setTaskCode(null);
      setFileToUpload(null);
      setNewFileName(job.taskId);
      setCurrentTab(JobRunnerTabTypes.CONFIG);
      setIsConfigEdited(false);
      setIsCodeEdited(false);
      setIsTemplateInfoEdited(false);
      await fetchTaskDetails(job.taskId);
    },
    [fetchTaskDetails]
  );

  const handleJobSelection = useCallback(
    async (job: JobInfoDTO | null) => {
      if (!job) return;

      if (!isAnyTabEdited) {
        await switchToJob(job);
      } else {
        confirmDialog({
          message: "You have unsaved changes. Do you want to save them?",
          header: "Unsaved Changes",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Save",
          rejectLabel: "Discard",
          acceptClassName: "p-button-danger",
          accept: async () => {
            await saveCurrentTab();
            await switchToJob(job);
          },
          reject: async () => {
            await switchToJob(job);
          },
        });
      }
    },
    [isAnyTabEdited, switchToJob, saveCurrentTab]
  );

  const handleAddNewJob = useCallback(
    async (newJob: JobInfoDTO) => {
      await apiJobs.createJob(newJob);
      await fetchJobs();
      NotificationPopUp.show("Job created successfully", "success");
    },
    [apiJobs, fetchJobs]
  );

  const handleRemoveJob = useCallback(async () => {
    if (!selectedJob) return;
    try {
      await apiJobs.deleteJob(selectedJob.taskId);
      NotificationPopUp.show("Job removed successfully!", "success");
      await fetchJobs();
      setSelectedJob(null);
    } catch (error) {
      NotificationPopUp.handleGenericError(error, "Failed to remove job.");
    } finally {
      closeDialog();
    }
  }, [apiJobs, selectedJob, fetchJobs, closeDialog]);

  const updateJob = useCallback(
    async (job: JobInfoDTO) => {
      await fetchJobs();
      setSelectedJob(job);
    },
    [fetchJobs]
  );

  // --- Tab change ---

  const handleCodeFileSelection = useCallback(
    async (code: ScheduledTaskExtendedDTO | null, tabType: JobRunnerTabTypes) => {
      if (tabType !== JobRunnerTabTypes.CODE) return;
      const freshCodeFiles = await fetchCodeFilesList();
      if (code?.codeFileId != null) {
        downloadTaskCode(code.codeFileId);
        const matchingCodeFile = freshCodeFiles?.find(
          (file) => file.id === code.codeFileId
        );
        if (matchingCodeFile) {
          setSelectedCodeFile(matchingCodeFile);
        }
      }
    },
    [fetchCodeFilesList, downloadTaskCode]
  );

  const persistEditorToState = useCallback(() => {
    const editorValue = jobRunnerViewRef.current?.editor?.getValue();
    if (!editorValue || !currentTask) return;
    try {
      const parsed = JSON.parse(editorValue);
      if (currentTab === JobRunnerTabTypes.TEMPLATE) {
        setCurrentTask({ ...currentTask, templateInfo: parsed } as ScheduledTaskExtendedDTO);
      } else if (currentTab === JobRunnerTabTypes.CONFIG) {
        setCurrentTask(parsed);
      }
    } catch {
      // invalid JSON — don't persist
    }
  }, [currentTab, currentTask]);

  const handleTabChange = useCallback(
    async (tabType: JobRunnerTabTypes) => {
      if (tabType === currentTab) return;

      if (!isAnyTabEdited) {
        persistEditorToState();
        handleCodeFileSelection(currentTask, tabType);
        setCurrentTab(tabType);
        if (currentTab === JobRunnerTabTypes.CODE && selectedJob) {
          await fetchTaskDetails(selectedJob.taskId);
        }
        setIsConfigEdited(false);
        setIsCodeEdited(false);
        setIsTemplateInfoEdited(false);
      } else {
        confirmDialog({
          message: "You have unsaved changes. Do you want to save them?",
          header: "Unsaved Changes",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Save",
          rejectLabel: "Discard",
          acceptClassName: "p-button-danger",
          accept: () => {
            if (currentTab === JobRunnerTabTypes.CONFIG) {
              saveTaskConfig();
              const config = jobRunnerViewRef.current?.editor?.getValue();
              const parsedConfig = JSON.parse(config);
              handleCodeFileSelection(parsedConfig, tabType);
            } else {
              saveCurrentTab();
            }
            setCurrentTab(tabType);
          },
          reject: () => {
            setIsConfigEdited(false);
            setIsCodeEdited(false);
            setIsTemplateInfoEdited(false);
            handleCodeFileSelection(currentTask, tabType);
            setCurrentTab(tabType);
          },
        });
      }
    },
    [
      currentTab,
      isAnyTabEdited,
      currentTask,
      selectedJob,
      persistEditorToState,
      handleCodeFileSelection,
      fetchTaskDetails,
      saveTaskConfig,
      saveCurrentTab,
    ]
  );

  // --- Dialog openers with data fetching ---

  const openTestDataDialogWithFetch = useCallback(async (referenceTime?: string) => {
    setOpenDialog("testData");
    if (!selectedJob) return;
    try {
      const response = await apiScheduled.getAirflowTaskTestData(
        "1",
        selectedJob.taskId,
        referenceTime ? { referenceTime } as any : undefined
      );
      setCurrentTestData(response.data);
    } catch (error) {
      NotificationPopUp.handleGenericError(
        error,
        "Failed to fetch test data."
      );
      setCurrentTestData(null);
    }
  }, [setOpenDialog, selectedJob, apiScheduled]);

  const openLogsDialogWithFetch = useCallback(
    async (taskId?: string) => {
      setLogsInitialTaskId(taskId ?? null);
      setOpenDialog("logs");
      try {
        const response = await apiJobRunnerLogs.getExecutions({});
        setCurrentExecutionsData(response.data);
        setLastExecutions(computeLastExecutions(response.data));
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          "Failed to fetch executions."
        );
        setCurrentExecutionsData(null);
      }
    },
    [setOpenDialog, apiJobRunnerLogs]
  );

  const handleFetchExecutions = useCallback(async () => {
    try {
      const response = await apiJobRunnerLogs.getExecutions({});
      setCurrentExecutionsData(response.data);
      setLastExecutions(computeLastExecutions(response.data));
    } catch (error) {
      NotificationPopUp.handleGenericError(
        error,
        "Failed to fetch executions."
      );
      setCurrentExecutionsData(null);
    }
  }, [apiJobRunnerLogs]);

  const openAssignCodeDialogWithFetch = useCallback(async () => {
    await fetchCodeFilesList();
    setOpenDialog("assignCode");
  }, [fetchCodeFilesList, setOpenDialog]);

  // --- Change handlers ---

  const setConfigEdited = useDataHubStore.getState().setIsConfigEdited;

  const handleScriptChange = useCallback(
    (updatedScript: string) => {
      setConfigEdited(true);
      setTaskCode(updatedScript);
      setIsCodeEdited(true);
    },
    [setConfigEdited]
  );

  const handleEncoderConfigUpdate = useCallback(
    (updatedConfig: any) => {
      setConfigEdited(true);
      setCurrentTask(updatedConfig);
      setIsConfigEdited(true);
    },
    [setConfigEdited]
  );

  const writerChanged = useCallback(
    (res: boolean) => {
      setConfigEdited(res);
      if (currentTab === JobRunnerTabTypes.TEMPLATE) {
        setIsTemplateInfoEdited(res);
      } else {
        setIsConfigEdited(res);
      }
    },
    [currentTab, setConfigEdited]
  );

  return {
    // State
    listLoad,
    jobs,
    selectedJob,
    currentTask,
    currentTab,
    templateInfo,
    codeFilesList,
    selectedCodeFile,
    setSelectedCodeFile,
    taskCode,
    encoderKey,
    fileToUpload,
    setFileToUpload,
    newFileName,
    setNewFileName,
    readScript,
    setReadScript,
    currentTestData,
    currentExecutionsData,
    lastExecutions,
    logsInitialTaskId,
    isConfigEdited,
    isCodeEdited,
    isTemplateInfoEdited,

    // Refs
    encoderRef,
    jobRunnerViewRef,

    // Actions
    fetchJobs,
    fetchCodeFilesList,
    runTask,
    saveTaskConfig,
    saveTaskCode,
    saveTemplateInfo,
    loadTaskCode,
    uploadTaskCode,
    createBlankPythonFile,
    createEmptyFileForJob,
    handleToggleJobRunner,
    handleJobSelection,
    handleAddNewJob,
    handleRemoveJob,
    updateJob,
    handleTabChange,
    handleScriptChange,
    handleEncoderConfigUpdate,
    writerChanged,

    // Dialog openers
    openTestDataDialogWithFetch,
    openLogsDialogWithFetch,
    openAssignCodeDialogWithFetch,
    handleFetchExecutions,
  };
};
